import {
  AcquiringChannel,
  Application,
  CardFeeDetailsV2DTO,
  Catalogue,
  CatalogueItem,
  DataStatus,
  Device,
  PaymentMethodAPI,
  PaymentMethodCode,
  PriceType,
  Solution,
  SolutionType,
} from '../types';
import {
  DEVICE_NAMES,
  PRODUCT_IMAGE_MAP,
  VIRTUAL_DEVICES,
} from 'src/features/order/tech-debt/product-details-extra';
import { getAcquiringChannel } from '../types/application/acquiring-channel';
import { MAX_DEVICES_PER_ORDER } from '../hooks/use-catalogue';
import { ProductDetailsAPI } from '../types/salesforce/api/products/product-details-api';
import { CardFee, CardFeesData, TerminalType } from '../types/application/card-fee';
import { CENTS_IN_ONE_RAND, getPriceWithVAT } from './currency';
import { DeliveryOptions } from 'src/features/common/types/application/delivery-options';
import { getFees, getFeesDPO } from './card-fees-helpers';
import { Environment } from 'src/Environment';
import { TerminalFee } from 'src/features/common/services/fees-service';
import MiniAppEvents from 'src/helpers/miniapp-events';

export const getCatalogue = (
  application: Application,
  products?: ProductDetailsAPI[],
  cardFeesDTO?: CardFeeDetailsV2DTO,
  paymentMethods?: PaymentMethodAPI[],
  solutions?: Solution[],
  terminalFees?: TerminalFee[],
): Catalogue => {
  const isInMiniApp = MiniAppEvents.isInMiniApp();

  if (products) {
    const productsExpandedByChannel: ProductDetailsAPI[] = products.flatMap((product) =>
      product.acquiringChannels.map((acquiringChannel, i, arr) => ({
        ...product,
        acquiringChannels: [acquiringChannel, ...arr],
      })),
    );

    const activeAcquiringChannels = getActiveAcquiringChannels(application);

    const filteredProducts = productsExpandedByChannel.filter((product) => {
      const acquiringChannelMatch =
        activeAcquiringChannels.indexOf(product.acquiringChannels[0] as AcquiringChannel) > -1;

      return acquiringChannelMatch;
    });

    const quantities = (application?.opportunity?.solutions || []).reduce((result, solution) => {
      result[getSolutionKey(solution.productId, solution.acquiringChannel)] = solution.quantity;
      return result;
    }, {});

    const catalogueItems = filteredProducts.map((product, index) => {
      const acquiringChannel = getAcquiringChannel(product.acquiringChannels[0]);
      const secondaryAcquiringChannel = getAcquiringChannel(product.acquiringChannels[1]);

      const currentSol = solutions?.find((sol) => sol.productId === product.productId);

      const quantity = quantities[getSolutionKey(product.productId, product.acquiringChannels[0])];
      const catalogueProduct: Device = {
        id: product.productId,
        acquiringChannel: acquiringChannel,
        secondaryAcquiringChannel: secondaryAcquiringChannel,
        title: product.productName,
        quantity: quantity || currentSol?.quantity,
        quantityOld: quantity || currentSol?.quantity,
        featureTextList: product.description,
        tagline: product.tagline,
        extraBenefits: product.extraBenefits,
        imageBase64: getImageFromProductName(product.productName),
        minQuantity: 0,
        isTenderType: !!product.productCategoryName && !product.isProductCategory,
        deliveryOption: getDeliveryOption(product.deliveryOptions || []) ?? DeliveryOptions.None,
        deliveryOptions: product.deliveryOptions || [],
        maxQuantity:
          product.isProductCategory ||
          product.productCategoryName ||
          product.productName === DEVICE_NAMES.TAP_ON_PHONE
            ? 1
            : undefined,
        isProductCategory: product.isProductCategory,
        productCategoryName: product.productCategoryName ?? '',
        isCardPresent: product.isCardPresent,
        serialNumbers: getSerialNumberForProduct(application, product),
        friendlyTerminalName: getDeviceFriendlyNameForProduct(application, product),
        allowedPaymentMethods: product.allowedPaymentMethods,
        isVirtual: VIRTUAL_DEVICES.includes(product.productName),
        isDeliverable:
          !VIRTUAL_DEVICES.includes(product.productName) &&
          ((product.deliveryOptions || []).includes(DeliveryOptions.Deliver) ||
            !product.deliveryOptions?.length),
      };

      const catalogueCard = {
        status: DataStatus.IDLE,
      };

      const cataloguePrice: CatalogueItem['price'] = (() => {
        if (acquiringChannel === AcquiringChannel.buyInstoreFicaOnline) {
          const price = 0;
          return {
            status: DataStatus.LOADED,
            price: price,
            priceType: PriceType.PAY_IN_STORE,
          };
        } else if (acquiringChannel === AcquiringChannel.thirdPartyChannel) {
          return {
            status: DataStatus.LOADED,
            price: getDevicePrice(product),
            priceType: getPriceType(product),
          };
        } else if (isDeviceHasPrice(product)) {
          const price = getDevicePrice(product);

          let type: PriceType = PriceType.ONCE_OFF;

          switch (acquiringChannel) {
            case AcquiringChannel.zeroRentOnlineFicaOnline:
              type = PriceType.FREE;
              break;
            case AcquiringChannel.rentOnlineFicaOnline:
              type = PriceType.MONTHLY;
          }

          return {
            status: DataStatus.LOADED,
            price: price,
            priceType: type,
          };
        } else {
          const price = 0;
          return {
            status: DataStatus.LOADED,
            price: price,
            priceType: PriceType.MONTHLY,
          };
        }
      })();

      if (terminalFees && terminalFees.length > 0) {
        catalogueProduct.isRental = catalogueProduct.allowedPaymentMethods.includes(
          PaymentMethodCode.RENTAL,
        );
      }

      if (catalogueProduct.isRental && isInMiniApp) {
        catalogueProduct.acquiringChannel = AcquiringChannel.rentOnlineFicaOnline;
        cataloguePrice.price = getRentalPrice(product, terminalFees);
        cataloguePrice.priceType = PriceType.MONTHLY;
        catalogueProduct.allowedPaymentMethods = [PaymentMethodCode.RENTAL];
      }

      if (catalogueProduct.isRental) {
        cataloguePrice.rentalPrice = getRentalPrice(product, terminalFees);
      }

      return {
        index: index,
        device: catalogueProduct,
        card: catalogueCard,
        price: cataloguePrice,
      } as CatalogueItem;
    });

    const isPricesLoaded = !catalogueItems.find(
      (catalogueItem) => catalogueItem.price.status !== DataStatus.LOADED,
    );

    const totals = catalogueItems
      .filter((catalogueItem) => catalogueItem?.device?.quantity > 0)
      .reduce(
        (totals, catalogueItem) => {
          switch (catalogueItem.price.priceType) {
            case PriceType.ONCE_OFF:
              return {
                ...totals,
                onceOff:
                  totals.onceOff +
                  (catalogueItem?.price?.price || 0) * catalogueItem.device.quantity,
              };

            case PriceType.MONTHLY:
              return {
                ...totals,
                monthly:
                  totals.monthly +
                  (catalogueItem?.price?.price || 0) * catalogueItem.device.quantity,
              };
          }

          return totals;
        },
        {
          onceOff: 0,
          monthly: 0,
        },
      );

    const cardFees = !Environment.current.disabledFeatures.dpo
      ? getFeesDPO(application.cart ?? [], cardFeesDTO)
      : getFees(catalogueItems, cardFeesDTO);
    const cardFeesCatalogue = cardFees ? cardFees.map(getCardFee) : undefined;
    const pricingTier = cardFees ? cardFees[0]?.pricing_tier_name : undefined;

    const catalogue: Catalogue = {
      products: catalogueItems,
      paymentMethods: paymentMethods,
      statusProducts: DataStatus.LOADED,
      statusAll: isPricesLoaded ? DataStatus.LOADED : DataStatus.LOADING,
      maxProductsPerOrder: MAX_DEVICES_PER_ORDER,
      cardFees: {
        fees: cardFeesCatalogue,
        pricingTier: pricingTier,
        status: cardFeesCatalogue ? DataStatus.LOADED : DataStatus.LOADING,
      },
      totals: {
        status: DataStatus.LOADED,
        onceOff: totals.onceOff,
        monthly: totals.monthly,
      },
    };

    return catalogue;
  } else {
    const catalogue: Catalogue = {
      products: [],
      statusAll: DataStatus.LOADING,
      statusProducts: DataStatus.LOADING,
      maxProductsPerOrder: MAX_DEVICES_PER_ORDER,
      cardFees: {
        status: DataStatus.IDLE,
      },
      totals: {
        status: DataStatus.LOADING,
      },
    };
    return catalogue;
  }
};

const CARD_PRESENT_TEXT = 'Card Present';

function getCardFee(cardFeeData: CardFeesData): CardFee {
  const cardFees: CardFee = {
    percentage: cardFeeData.percentage,
    cardProviderName: `${cardFeeData.demographic} - ${cardFeeData.product_name} -  ${cardFeeData.card_type}`,
    terminalType:
      cardFeeData.fee_category === CARD_PRESENT_TEXT
        ? TerminalType.CARD_PRESENT
        : TerminalType.CARD_NOT_PRESENT,
  };
  return cardFees;
}

function getSolutionKey(deviceId, acquiringChannel) {
  return `${deviceId}${acquiringChannel}`;
}

function getDeliveryOption(deliveryOptions: DeliveryOptions[]): DeliveryOptions | null {
  if (deliveryOptions.length) {
    return (deliveryOptions[0]?.toLowerCase() === 'deliver' && deliveryOptions[0]) || null;
  }

  return null;
}

function getActiveAcquiringChannels(application: Application): AcquiringChannel[] {
  const acquiringChannels: AcquiringChannel[] = [];

  switch (application.solutionType) {
    case SolutionType.buyInStoreFicaOnline:
      acquiringChannels.push(AcquiringChannel.buyInstoreFicaOnline);
      break;

    case SolutionType.vodacomBusiness:
      acquiringChannels.push(AcquiringChannel.vodacomBusiness);
      break;

    case SolutionType.buyInStoreFicaInstore:
      acquiringChannels.push(AcquiringChannel.buyInstoreFicaInstore);
      break;

    case SolutionType.buyOrRentOnlineFicaOnline:
      acquiringChannels.push(AcquiringChannel.buyOnlineFicaOnline);
      acquiringChannels.push(AcquiringChannel.rentOnlineFicaOnline);
      acquiringChannels.push(AcquiringChannel.zeroRentOnlineFicaOnline);
      break;

    case SolutionType.thirdPartyOnboarding:
      acquiringChannels.push(AcquiringChannel.thirdPartyChannel);
      break;

    case SolutionType.vodapay:
      acquiringChannels.push(AcquiringChannel.rentOnlineFicaOnline);
      acquiringChannels.push(AcquiringChannel.zeroRentOnlineFicaOnline);
      break;
  }
  return acquiringChannels;
}

function getDevicePrice(product: ProductDetailsAPI) {
  return getPriceWithVAT(product.retailFeeVatExclusive || 0);
}

function isDeviceHasPrice(product: ProductDetailsAPI) {
  const devicePrice = getDevicePrice(product);
  const acquiringChannel = getAcquiringChannel(product.acquiringChannels[0]);
  return (
    [
      AcquiringChannel.buyOnlineFicaOnline,
      AcquiringChannel.rentOnlineFicaOnline,
      AcquiringChannel.zeroRentOnlineFicaOnline,
      AcquiringChannel.vodapay,
      AcquiringChannel.thirdPartyChannel,
    ].includes(acquiringChannel) &&
    (devicePrice ||
      devicePrice === 0 ||
      (product.retailFeeVatExclusive && acquiringChannel === AcquiringChannel.buyOnlineFicaOnline))
  );
}

function getImageFromProductName(product: string): string {
  return PRODUCT_IMAGE_MAP[product] || '';
}

function getPriceType(product: ProductDetailsAPI): PriceType {
  if (product.acquiringChannels.includes(AcquiringChannel.buyOnlineFicaOnline)) {
    return PriceType.ONCE_OFF;
  }

  if (product.acquiringChannels.includes(AcquiringChannel.rentOnlineFicaOnline)) {
    return PriceType.MONTHLY;
  }

  if (product.acquiringChannels.includes(AcquiringChannel.buyInstoreFicaOnline)) {
    return PriceType.PAY_IN_STORE;
  }

  return PriceType.FREE;
}

function getRentalPrice(
  product: ProductDetailsAPI,
  terminalFees?: TerminalFee[],
): number | undefined {
  const terminalFee = terminalFees?.find((fee) => fee.deviceModelName === product.productName);

  return terminalFee?.fixedFee
    ? getPriceWithVAT(terminalFee.fixedFee * CENTS_IN_ONE_RAND)
    : undefined;
}

function getSerialNumberForProduct(application: Application, product: ProductDetailsAPI): string[] {
  const solution = application.opportunity?.solutions?.find(
    (solution) => solution.productId === product.productId,
  );

  return solution?.serialNumbers || [];
}

function getDeviceFriendlyNameForProduct(
  application: Application,
  product: ProductDetailsAPI,
): string[] {
  const solution = application.opportunity?.solutions?.find(
    (solution) => solution.productId === product.productId,
  );

  return solution?.friendlyTerminalName || [];
}
